import { useCallback, useMemo, useState } from 'react'
import { SelectedOrgChecker } from '@features/organisations/components/SelectedOrgChecker'
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { z } from 'zod'
import { EmptyState } from '@features/multiview/components/EmptyState'
import { Multiview } from '@features/multiview/components/Multiview'
import { SessionSelector } from '@features/multiview/components/SessionSelector'
import { MultiviewErrorBoundary } from '@features/multiview/components/MultiviewErrorBoundary'
import {
  parsePersistedState,
  encodePersistedState,
  saveToSessionStorageV2,
  loadFromSessionStorageV2,
  loadSnapshotCache,
  hydrateSnapshots,
} from '@features/multiview/utils/persistence'
import type { Tile } from '@features/multiview/types'

const multiviewSearchSchema = z
  .object({
    feeds: z.string().optional(),
  })
  .partial()
  .catch({})

const MultiviewContent = () => {
  const { orgId } = useParams({ strict: false })
  const navigate = useNavigate()
  const search = useSearch({ from: Route.id })

  const { tiles, colors } = useMemo(() => {
    const parsed = parsePersistedState(search.feeds)
    if (orgId) {
      const cache = loadSnapshotCache(orgId)
      return {
        tiles: hydrateSnapshots(parsed.tiles, cache),
        colors: parsed.colors,
      }
    }
    return parsed
  }, [search.feeds, orgId])

  const persistState = useCallback(
    (nextTiles: Tile[], nextColors: Record<string, number>) => {
      if (!orgId) return
      saveToSessionStorageV2(orgId, nextTiles, nextColors)
      const encoded = encodePersistedState(nextTiles, nextColors)
      navigate({
        to: '.',
        search: (prev) => {
          if (encoded === '') {
            const { feeds: _, ...rest } = prev as { feeds?: string }
            return rest
          }
          return { ...prev, feeds: encoded }
        },
        replace: true,
      })
    },
    [orgId, navigate]
  )

  const setTiles = useCallback(
    (newTiles: Tile[]) => {
      persistState(newTiles, colors)
    },
    [persistState, colors]
  )

  const setColors = useCallback(
    (newColors: Record<string, number>) => {
      persistState(tiles, newColors)
    },
    [persistState, tiles]
  )

  const [isSessionSelectorOpen, setIsSessionSelectorOpen] = useState(false)

  const handleAddSessions = useCallback(() => {
    setIsSessionSelectorOpen(true)
  }, [])

  const handleCloseSessionSelector = useCallback(() => {
    setIsSessionSelectorOpen(false)
  }, [])

  return (
    <>
      {tiles.length === 0 ? (
        <EmptyState onAddSessions={handleAddSessions} />
      ) : (
        <Multiview
          tiles={tiles}
          onTilesChange={setTiles}
          colors={colors}
          onColorsChange={setColors}
        />
      )}

      <SessionSelector
        isOpen={isSessionSelectorOpen}
        onClose={handleCloseSessionSelector}
        tiles={tiles}
        onTilesChange={setTiles}
      />
    </>
  )
}

const MultiviewRoute = () => {
  return (
    <SelectedOrgChecker>
      <MultiviewErrorBoundary>
        <MultiviewContent />
      </MultiviewErrorBoundary>
    </SelectedOrgChecker>
  )
}

export const Route = createFileRoute('/_auth/o/$orgId/multiview/')({
  component: MultiviewRoute,
  validateSearch: zodValidator(multiviewSearchSchema),
  beforeLoad: ({ search, params }) => {
    if (!search.feeds) {
      const stored = loadFromSessionStorageV2(params.orgId)
      if (stored.tiles.length > 0) {
        throw redirect({
          to: '/o/$orgId/multiview',
          params: { orgId: params.orgId },
          search: { feeds: encodePersistedState(stored.tiles, stored.colors) },
          replace: true,
        })
      }
    }
    return { getTitle: () => 'Multiview' }
  },
})
